import { Component, EventEmitter, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { ConfirmationService, MessageService, MenuItem, TreeNode } from 'primeng/api';
import { HttpEventType } from '@angular/common/http';

import { Observable } from 'rxjs';
import { Download } from '../../../download/download';

import { MainFacade } from '../../main.facade';
import { FileFolder, IFile, IFileInfo, ImageFile } from '../../models/file-info';
import { SpatialOmicsService } from '../../../services/spatial-omics.service';
import { AnnotationsService } from '../../../services/annotations.service';

@Component({
  selector: 'file-tree',
  templateUrl: './file-tree.component.html',
  styleUrls: ['./file-tree.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class FileTreeComponent implements OnInit {
  // necessary to enable type checking within the component's template
  public readonly IFile = IFile;

  public selectedNode!: TreeNode | TreeNode[] | null;
  public nodeToLoad!: TreeNode | TreeNode[] | null;
  public loadingTree!: boolean;
  public loadingImage!: boolean;

  @Output() interuptPreview = new EventEmitter<null>();
  @Output() processSelected = new EventEmitter<null>();
  @Output() nodeExpand = new EventEmitter<TreeNode>();
  @Output() nodeSelect = new EventEmitter<TreeNode>();

  public treeMenu: MenuItem[] = [];
  public nodes: TreeNode<IFileInfo>[] = [];
  public download$: Observable<Download> | null = null;
  // protected readonly download = download;
  public downloadingFileName = '';
  public showDownloadDialog = false;
  public downloadProgress = 0;


  constructor(private facade: MainFacade,
              private confirmationService: ConfirmationService,
              private messageService: MessageService) {
    // do nothing
  }

  ngOnInit(): void {
    this.manageSubscriptions();
    this.treeMenu = this.createTreeMenu(null);
  }

  manageSubscriptions(): void {
    this.facade.getRootNodes$().subscribe(
      (rootNodes) => {
        this.nodes = rootNodes;
      }
    );
    this.facade.getSelectedNode$().subscribe({
      next: (node) => {
        this.selectedNode = node;
      },
      error: (err) => {
        console.error(`Error with node selection: ${err}`);
      },
    });
    this.facade.isTreeLoading$().subscribe({
      next: (isLoading) => {
        this.loadingTree = isLoading;
      },
    });
    // Highlights a node (e.g. a Queue-tab "View Results" that already
    // displayed the file via the fast path) without going through
    // getSelectedNode$() - that's also what drives the viewer, and it's
    // already showing the file, so reusing it here would reload it a
    // second time. See MainState.getHighlightNode$().
    this.facade.getHighlightNode$().subscribe({
      next: (node) => {
        this.selectedNode = node;
      },
    });
  }

  createTreeMenu(node: TreeNode<IFileInfo> | null): MenuItem[] {
    return [
      {
        label: 'Delete',
        icon: 'pi pi-trash',
        command: (event) => this.onMenuOptionDelete(),
      },
      {
        label: 'Refresh',
        icon: 'pi pi-refresh',
        command: (event) => this.onMenuOptionRefresh(),
      },
      {
        label: 'Download',
        icon: 'pi pi-download',
        command: (event) => this.onMenuOptionDownload(event),
        disabled: !this.canDownload(node),
      },
      {
        // Opt-in: load this image plus its numbered same-name siblings
        // (image_1..image_N) as one z-stack. A plain click loads just the file.
        label: 'Load as Stack',
        icon: 'pi pi-images',
        command: () => this.onMenuOptionLoadAsStack(),
        disabled: !this.canLoadAsStack(node),
      },
      {
        // A 10x Xenium bundle: a *_xe_outs.zip, or a folder holding experiment.xenium.
        label: 'Open in Spatial Omics plot mode',
        icon: 'pi pi-th-large',
        command: () => this.onMenuOptionOpenSpatialOmics(),
        disabled: !SpatialOmicsService.canOpen(node?.data),
      },
      {
        // Annotations by themselves, with no image: a label volume (with its ontology beside it),
        // a .zip or .geojson, or a folder (per-slice GeoJSON, a label volume, an OME-Zarr store).
        label: 'Open as annotations',
        icon: 'pi pi-sitemap',
        command: () => this.onMenuOptionOpenAnnotations(),
        disabled: !AnnotationsService.canOpen(node?.data),
      }
    ];
  }

  canDownload(node: TreeNode<IFileInfo> | null): boolean {
    if (node === null || node.data === null) {
      return false;
    } else if (node.data instanceof IFile) {
      return true;
    }
    return false;
  }

  /** "Load as Stack" applies to image files (it detects the numbered series on
   *  action; enabling it per-node without a folder listing would be too costly). */
  canLoadAsStack(node: TreeNode<IFileInfo> | null): boolean {
    return !!node && node.data instanceof ImageFile;
  }

  onMenuOptionDelete() {
    const node = !Array.isArray(this.selectedNode) ? this.selectedNode : null;
    const name = node?.data?.name || 'this item';
    this.confirmationService.confirm({
      message: `Are you sure you want to delete "${name}"?`,
      header: 'Delete Confirmation',
      icon: 'pi pi-info-circle',
      accept: () => {
        if (node) {
          this.facade.deleteNode(node);
        }
      }
    });
  }

  onMenuOptionRefresh() {
    if (this.selectedNode && !Array.isArray(this.selectedNode) && this.selectedNode.children) {
      this.selectedNode.children.length = 0;
    }
    this.nodeToLoad = this.selectedNode;
    this.facade.updateNode(this.nodeToLoad);
  }

  onMenuOptionDownload(event: any) {
    if (this.selectedNode && !Array.isArray(this.selectedNode) && this.selectedNode.data) {
      this.downloadingFileName = this.selectedNode.data.name;
      this.showDownloadDialog = true;
      this.downloadProgress = 0;

      this.facade.download()?.subscribe({
        next: (event: any) => {
          if (event.type === HttpEventType.DownloadProgress && event.total) {
            this.downloadProgress = Math.round((event.loaded / event.total) * 100);
          } else if (event.type === HttpEventType.Response) {
            const blob = new Blob([event.body], { type: event.body.type });
            if (this.selectedNode && !Array.isArray(this.selectedNode) && this.selectedNode.data) {
              const downloadName = this.selectedNode.data.name;
              this.facade.save(blob, downloadName);
              this.showDownloadDialog = false;
              this.downloadProgress = 0;
              this.messageService.add({ severity: 'success', summary: 'Download completed', detail: `${downloadName} download completed.`, life: 3000 });
            }
          }
        },
        error: (err: any) => {
          if (err.name === 'AbortError') {
            this.facade.cancelDownload();
            this.messageService.add({ severity: 'warn', summary: 'Download Cancelled', detail: 'File download cancelled.', life: 3000 });
          } else {
            this.messageService.add({ severity: 'error', summary: 'Download failed', detail: `The following error occurred: ${err}.`, life: 3000 });
          }
          this.showDownloadDialog = false;
          this.downloadProgress = 0;
        },
      });
    }
  }

  onCancelDownload() {
    this.facade.cancelDownload();
    this.showDownloadDialog = false;
    this.downloadProgress = 0;
  }

  onNodeExpand(event: any): void {
    if (!this.loadingTree){
      this.nodeToLoad = event.node;
      this.nodeExpand.emit(event.node);
    } else {
      event.node.collapsed = true;
      event.node.expanded = false;
    }
  }

  onNodeCollapse(event: any): void {
    event.node.children.length = 0;
  }

  onNodeContextMenuSelect(event: any): void {
    this.selectedNode = event.node;
    const downloadItem = this.treeMenu.find(m => m.label === 'Download');
    if (downloadItem) {
      downloadItem.disabled = !this.canDownload(event.node);
    }
    const loadAsStackItem = this.treeMenu.find(m => m.label === 'Load as Stack');
    if (loadAsStackItem) {
      loadAsStackItem.disabled = !this.canLoadAsStack(event.node);
    }
    const spatialItem = this.treeMenu.find(m => m.label === 'Open in Spatial Omics plot mode');
    if (spatialItem) {
      spatialItem.disabled = !SpatialOmicsService.canOpen(event.node?.data);
    }
    const annotationsItem = this.treeMenu.find(m => m.label === 'Open as annotations');
    if (annotationsItem) {
      annotationsItem.disabled = !AnnotationsService.canOpen(event.node?.data);
    }
  }

  onMenuOptionOpenAnnotations(): void {
    const node = !Array.isArray(this.selectedNode) ? this.selectedNode : null;
    if (node) {
      this.facade.openAsAnnotations(node);
    }
  }

  onMenuOptionOpenSpatialOmics(): void {
    const node = !Array.isArray(this.selectedNode) ? this.selectedNode : null;
    if (node) {
      this.facade.openInSpatialOmics(node);
    }
  }

  onMenuOptionLoadAsStack(): void {
    const node = !Array.isArray(this.selectedNode) ? this.selectedNode : null;
    if (node) {
      this.facade.loadAsStack(node);
    }
  }

  onNodeSelect(event: any): void {
    this.nodeSelect.emit(event.node);
  }

  onLoadMore(event: Event, node: TreeNode): void {
    event.stopPropagation();
    const folderNode = node.parent as TreeNode<IFileInfo>;
    // Keep the parent folder visually selected so its spinner shows during load.
    this.selectedNode = folderNode;
    this.facade.loadMoreChildren(folderNode, node.data.nextPage);
  }

  isFolder(file: IFileInfo){
    return file instanceof FileFolder;
  }

  isSelectedNode(node: any){
    return node === this.selectedNode;
  }

  showFolderLoader(node: any){
    return this.isFolder(node.data) && this.loadingTree && this.isSelectedNode(node);
  }
}
